/**
 * Select — Radix Select for the closed set: pick one of a handful of options.
 *
 * `searchable` hands off to `Combobox`, which is the same visual object with a
 * filter field and a virtualised list behind it. Both share the listbox surface
 * and row recipes so the two popups are indistinguishable to the eye.
 */

import { forwardRef, useMemo } from 'react';
import type { ReactNode } from 'react';
import * as RSelect from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { ControlSize } from './Button';
import { FIELD_PAD, fieldShell } from './Input';
import { Combobox, GROUP_LABEL, LISTBOX_SURFACE, OPTION_ROW, groupOptions } from './Combobox';

export interface SelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
  /** Second line inside the option row — the "why you'd pick this". */
  description?: string;
  /** Options sharing a group render under one eyebrow heading, in first-seen order. */
  group?: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string | null;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  size?: ControlSize;
  invalid?: boolean;
  disabled?: boolean;
  /** Swap in the filterable listbox. Worth it past ~12 options. */
  searchable?: boolean;
  name?: string;
  required?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
  contentClassName?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    options,
    value,
    onValueChange,
    placeholder = 'Select…',
    size = 'md',
    invalid = false,
    disabled = false,
    searchable = false,
    name,
    required,
    id,
    className,
    contentClassName,
    align = 'start',
    side = 'bottom',
    ...aria
  },
  ref,
) {
  const groups = useMemo(() => groupOptions(options), [options]);
  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  if (searchable) {
    return (
      <Combobox
        ref={ref}
        options={options}
        value={value ?? null}
        onValueChange={onValueChange}
        placeholder={placeholder}
        size={size}
        invalid={invalid}
        disabled={disabled}
        id={id}
        className={className}
        contentClassName={contentClassName}
        align={align}
        side={side}
        {...aria}
      />
    );
  }

  return (
    <RSelect.Root
      value={value ?? undefined}
      onValueChange={onValueChange}
      disabled={disabled}
      name={name}
      required={required}
    >
      <RSelect.Trigger
        ref={ref}
        id={id}
        aria-invalid={invalid || undefined}
        className={cn(
          'group',
          fieldShell({ size, invalid, disabled, focus: 'visible' }),
          FIELD_PAD[size],
          'justify-between text-left',
          'data-[state=open]:border-focus data-[state=open]:shadow-[inset_0_0_0_1px_var(--focus)]',
          className,
        )}
        {...aria}
      >
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          {selected?.icon ? (
            <span className="flex shrink-0 items-center text-muted [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:-translate-y-[0.5px]">
              {selected.icon}
            </span>
          ) : null}
          <RSelect.Value
            placeholder={<span className="text-muted">{placeholder}</span>}
            className="truncate"
          />
        </span>
        <RSelect.Icon asChild>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'size-3.5 shrink-0 text-muted',
              'transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              'group-data-[state=open]:rotate-180',
            )}
          />
        </RSelect.Icon>
      </RSelect.Trigger>

      <RSelect.Portal>
        <RSelect.Content
          position="popper"
          sideOffset={4}
          align={align}
          side={side}
          className={cn(
            LISTBOX_SURFACE,
            'max-h-[min(20rem,var(--radix-select-content-available-height))]',
            'min-w-[var(--radix-select-trigger-width)]',
            contentClassName,
          )}
          style={{ animation: 'af-scale-in var(--dur-fast) var(--ease-out)' }}
        >
          <RSelect.ScrollUpButton className="flex h-5 items-center justify-center text-muted">
            <ChevronUp aria-hidden="true" className="size-3.5" />
          </RSelect.ScrollUpButton>

          <RSelect.Viewport className="p-1">
            {groups.map(([groupName, groupOptionList], groupIndex) => (
              <RSelect.Group key={groupName || `ungrouped-${groupIndex}`}>
                {groupName ? (
                  <>
                    {groupIndex > 0 ? (
                      <RSelect.Separator className="my-1 h-px bg-line-subtle" />
                    ) : null}
                    <RSelect.Label className={cn(GROUP_LABEL, 'h-7')}>{groupName}</RSelect.Label>
                  </>
                ) : null}

                {groupOptionList.map((option) => (
                  <RSelect.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={cn(
                      OPTION_ROW,
                      option.description
                        ? 'min-h-10 py-1.5'
                        : 'min-h-[var(--h-control-sm)] py-1',
                      'data-[highlighted]:bg-hover',
                      'data-[disabled]:pointer-events-none data-[disabled]:text-muted',
                    )}
                  >
                    {option.icon ? (
                      <span className="flex shrink-0 items-center text-muted [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:-translate-y-[0.5px]">
                        {option.icon}
                      </span>
                    ) : null}
                    <span className="min-w-0 flex-1">
                      <RSelect.ItemText>
                        <span className="block truncate">{option.label}</span>
                      </RSelect.ItemText>
                      {option.description ? (
                        <span className="mt-px block truncate text-xs text-muted">
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                    <RSelect.ItemIndicator className="absolute right-2 flex items-center">
                      <Check aria-hidden="true" className="size-3.5 text-accent" strokeWidth={2.5} />
                    </RSelect.ItemIndicator>
                  </RSelect.Item>
                ))}
              </RSelect.Group>
            ))}
          </RSelect.Viewport>

          <RSelect.ScrollDownButton className="flex h-5 items-center justify-center text-muted">
            <ChevronDown aria-hidden="true" className="size-3.5" />
          </RSelect.ScrollDownButton>
        </RSelect.Content>
      </RSelect.Portal>
    </RSelect.Root>
  );
});
